import type { BusinessDaySnapshot } from '../business-day/business-day-authority.ts';
import type { OperationalFact } from '../facts/operational-facts.ts';
import type { StaffPermission } from '../staff/staff-authority.ts';
import type { TableAuthorityProjection } from '../table/table-authority.ts';
import { buildDashboardComparisonFromFacts } from '../reporting/dashboard-comparison.ts';
import { buildReportExport, queryHistoricalReport } from '../reporting/reporting-query.ts';
import { projectPeriodReport, projectTodayOperations } from '../reporting/reporting-projection.ts';
import type {
  SmtReportingQuery,
  SmtReportingQueryContext,
  SmtReportingQueryResult,
} from './smt-reporting-contract.ts';

export interface SmtReportingStaffAuthorizationRequest {
  readonly actorId: string;
  readonly permission: Extract<StaffPermission, 'report.view.history' | 'report.export'>;
  readonly at: string;
}

export interface CreateSmtReportingSurfaceDependencies {
  readonly getFacts: () => readonly OperationalFact[];
  readonly getBusinessDay: () => BusinessDaySnapshot | null;
  readonly getTable: () => TableAuthorityProjection | null;
  readonly authorizeStaff?: (request: SmtReportingStaffAuthorizationRequest) => void;
}

const assertContext = (context: SmtReportingQueryContext): void => {
  if (!context.actorId?.trim()) throw new Error('SMT_REPORTING_ACTOR_REQUIRED');
  if (!Number.isFinite(Date.parse(context.at))) throw new Error('SMT_REPORTING_TIMESTAMP_INVALID');
};

const requireStaffAuthorization = (deps: CreateSmtReportingSurfaceDependencies) => {
  if (!deps.authorizeStaff) throw new Error('SMT_REPORTING_STAFF_AUTHORITY_REQUIRED');
  return deps.authorizeStaff;
};

const sourceIdentityOf = (facts: readonly OperationalFact[]): string => {
  if (facts.length === 0) return 'facts:0:empty';
  return `facts:${facts.length}:${facts[0]!.eventId}:${facts[facts.length - 1]!.eventId}`;
};

const operatingBusinessDates = (facts: readonly OperationalFact[]): readonly string[] => [...new Set(
  facts.filter((fact) => fact.factType === 'day_close.receipt').map((fact) => fact.businessDate),
)].sort();

const knownReportingCode = (error: unknown): string | null => {
  if (!(error instanceof Error)) return null;
  if (error.message.startsWith('REPORTING_')) return error.message;
  if (error.message.startsWith('STAFF_')) return error.message;
  return null;
};

export function createSmtReportingSurface(deps: CreateSmtReportingSurfaceDependencies) {
  const query = (
    request: SmtReportingQuery,
    context: SmtReportingQueryContext,
  ): SmtReportingQueryResult => {
    assertContext(context);
    const facts = deps.getFacts();

    try {
      switch (request.kind) {
        case 'report.today': {
          const businessDay = deps.getBusinessDay();
          if (!businessDay) throw new Error('REPORTING_BUSINESS_DAY_REQUIRED');
          const table = deps.getTable();
          if (!table) throw new Error('SMT_REPORTING_TABLE_AUTHORITY_REQUIRED');
          return {
            kind: 'accepted',
            queryKind: request.kind,
            report: projectTodayOperations({ facts, businessDay, table, now: context.at }),
          };
        }
        case 'report.period': {
          const authorize = requireStaffAuthorization(deps);
          const report = queryHistoricalReport({
            actorId: context.actorId,
            at: context.at,
            authorize,
            build: () => projectPeriodReport({
              facts,
              businessDates: request.businessDates,
              historicalMode: request.historicalMode,
            }),
          });
          return { kind: 'accepted', queryKind: request.kind, report };
        }
        case 'report.dashboard-comparison': {
          const authorize = requireStaffAuthorization(deps);
          const report = queryHistoricalReport({
            actorId: context.actorId,
            at: context.at,
            authorize,
            build: () => buildDashboardComparisonFromFacts({
              facts,
              currentBusinessDate: request.currentBusinessDate,
              currentCutoffAt: request.currentCutoffAt,
              operatingBusinessDates: operatingBusinessDates(facts),
            }),
          });
          return { kind: 'accepted', queryKind: request.kind, report };
        }
        case 'report.period.export': {
          const authorize = requireStaffAuthorization(deps);
          const report = projectPeriodReport({
            facts,
            businessDates: request.businessDates,
            historicalMode: request.historicalMode,
          });
          const exported = buildReportExport({
            actorId: context.actorId,
            at: context.at,
            authorize,
            generatedAt: context.at,
            report,
            filters: {
              businessDates: [...request.businessDates],
              historicalMode: request.historicalMode,
            },
            sourceIdentity: sourceIdentityOf(facts),
            freshness: 'fresh',
          });
          return { kind: 'accepted', queryKind: request.kind, export: exported };
        }
        default: {
          const exhaustive: never = request;
          throw new Error(`SMT_REPORTING_QUERY_UNSUPPORTED:${String(exhaustive)}`);
        }
      }
    } catch (error: unknown) {
      const code = knownReportingCode(error);
      if (!code) throw error;
      return { kind: 'rejected', queryKind: request.kind, code };
    }
  };

  return { query };
}
